import { useCallback, useMemo, useState, type MouseEvent } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { browserHistory } from 'react-router'
import useSWR from 'swr'

import { menuFetcher, type MenuQueryInput, type MenuResponse } from '@library/api-menu-experience'
import type { CollectionSlug, CollectionUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import { recipeCollectionSelected } from 'actions/trackingKeys'
import type {
  BrowseSelectionState,
  SelectBrowseEvent,
  SelectCollectionEvent,
} from 'routes/Menu/MenuExperience/types'
import { CollectionSlug as MenuCollectionSlug } from 'routes/Menu/domains/collections'
import { getIsMenuContextLoaded } from 'routes/Menu/selectors/menu'
import { getBasketDate, getNumPortions } from 'selectors/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { getMenuExperiencePreview } from '../../../context/previewContext'

type MenuExperienceQuery = MenuQueryParams & Record<string, unknown> & { collection?: string }

export type MenuExperienceCategorySelectionMethod =
  SelectCollectionEvent['properties']['navigation_method']

export type MenuExperienceMenuSWRKey = {
  key: 'menu-experience-menu'
  input: MenuQueryInput
}

export type MenuExperienceNavItem = {
  id: string
  text: string
  slug: string
  selected: boolean
  onClick: (event?: MouseEvent<HTMLElement>) => void
}

export type MenuExperienceCategoryNavigationTarget = {
  id: string
  slug: string
}

type CategoryAnalyticsProperties = Pick<SelectCollectionEvent['properties'], 'merch_header_type'>

export type MenuExperienceCategorySelect = (
  category: MenuExperienceCategoryNavigationTarget,
  navigationMethod: MenuExperienceCategorySelectionMethod,
  parentCategoryId?: string,
  analyticsProperties?: CategoryAnalyticsProperties,
) => void

type MenuExperienceCategory = MenuResponse['categories'][number]

const NO_CATEGORIES: MenuExperienceCategory[] = []

const toNavItem = (
  category: MenuExperienceCategoryNavigationTarget,
  text: string,
  selectedNavSlug: string | undefined,
  selectCategory: MenuExperienceCategorySelect,
): MenuExperienceNavItem => ({
  id: category.id,
  text,
  slug: category.slug,
  selected: category.slug === selectedNavSlug,
  onClick: (event) => {
    event?.preventDefault()
    selectCategory(category, 'navigation_bar')
  },
})

const fetchMenuExperience = ({ input }: MenuExperienceMenuSWRKey) => menuFetcher(input)

const getSelectedCategorySlug = (query: MenuExperienceQuery) =>
  typeof query.collection === 'string' && query.collection.trim().length > 0
    ? query.collection
    : undefined

type MenuExperienceMenuSWRKeyInput = {
  deliveryDate?: string
  numPortions?: number
  query: MenuExperienceQuery
}

export const getMenuExperienceMenuSWRKey = ({
  deliveryDate,
  numPortions,
  query,
}: MenuExperienceMenuSWRKeyInput): MenuExperienceMenuSWRKey | null => {
  const preview = getMenuExperiencePreview(query)
  const selectedCategorySlug = getSelectedCategorySlug(query)

  return deliveryDate && numPortions
    ? {
        key: 'menu-experience-menu',
        input: {
          deliveryDate,
          numPortions,
          ...(selectedCategorySlug && { categorySlug: selectedCategorySlug }),
          ...(preview && { preview }),
        },
      }
    : null
}

export const useMenuExperienceNavigation = (query: MenuExperienceQuery, pathname = '/menu') => {
  const dispatch = useDispatch()
  const [browseSelectionState, setBrowseSelectionState] = useState<BrowseSelectionState>(null)
  const deliveryDate = useSelector(getBasketDate)
  const isMenuContextLoaded = useSelector(getIsMenuContextLoaded)
  const numPortions = useSelector(getNumPortions)
  const selectedCategorySlug = getSelectedCategorySlug(query)
  const menuSWRKey = isMenuContextLoaded
    ? getMenuExperienceMenuSWRKey({ deliveryDate, numPortions, query })
    : null

  const { data: menuData, error: menuError } = useSWR(menuSWRKey, fetchMenuExperience)

  const selectedNavSlug = selectedCategorySlug ?? menuData?.categorySlug

  const currentCategoryId = menuData?.categoryId
  const currentCategorySlug = menuData?.categorySlug
  const menuId = menuData?.menuId

  const clearBrowseSelectionState = useCallback(() => {
    setBrowseSelectionState(null)
  }, [])

  const trackSelectBrowse = useCallback(
    (navigationMethod: SelectBrowseEvent['properties']['navigation_method']) => {
      if (!menuId) return

      queueTrackingEvent<SelectBrowseEvent>({
        action: 'select_browse',
        properties: {
          menu_uuid: menuId,
          navigation_method: navigationMethod,
        },
      })
    },
    [menuId],
  )

  const pushCategory = useCallback(
    (slug: string) => {
      browserHistory.push({
        pathname,
        query: {
          ...query,
          collection: slug,
        },
      })
    },
    [pathname, query],
  )

  const selectCategory: MenuExperienceCategorySelect = useCallback(
    (category, navigationMethod, parentCategoryId, analyticsProperties) => {
      if (navigationMethod === 'browse') {
        setBrowseSelectionState(
          parentCategoryId
            ? {
                childCollectionId: category.id as CollectionUUID,
                parentCollectionId: parentCategoryId as CollectionUUID,
              }
            : null,
        )
      }

      if (category.slug === MenuCollectionSlug.Browse) {
        trackSelectBrowse('navigation_bar')
      } else {
        queueTrackingEvent<SelectCollectionEvent>({
          action: 'select_collection',
          properties: {
            collection_id: category.id as CollectionUUID,
            collection_slug: category.slug as CollectionSlug,
            ...(currentCategoryId &&
              currentCategorySlug && {
                current_collection_id: currentCategoryId as CollectionUUID,
                current_collection_slug: currentCategorySlug as CollectionSlug,
              }),
            ...(parentCategoryId && { parent_collection_id: parentCategoryId as CollectionUUID }),
            ...analyticsProperties,
            navigation_method: navigationMethod,
          },
        })

        if (category.id !== currentCategoryId) {
          dispatch({
            type: actionTypes.TRACKING,
            trackingData: {
              actionType: recipeCollectionSelected,
              collectionId: category.id,
              fromCollectionId: currentCategoryId ?? null,
            },
          })
        }
      }

      pushCategory(category.slug)
    },
    [currentCategoryId, currentCategorySlug, dispatch, pushCategory, trackSelectBrowse],
  )

  const categories = menuData?.categories ?? NO_CATEGORIES

  const links = useMemo<MenuExperienceNavItem[]>(
    () =>
      categories.map((category) =>
        toNavItem(category, category.title, selectedNavSlug, selectCategory),
      ),
    [categories, selectCategory, selectedNavSlug],
  )

  const backLink = useMemo<MenuExperienceNavItem | undefined>(() => {
    const browse = categories.find(({ slug }) => slug === MenuCollectionSlug.Browse)

    if (!browse) return undefined

    return {
      id: browse.id,
      text: 'Back',
      slug: browse.slug,
      selected: false,
      onClick: (event) => {
        event?.preventDefault()
        trackSelectBrowse('back_button')
        pushCategory(browse.slug)
      },
    }
  }, [categories, pushCategory, trackSelectBrowse])

  return {
    data: menuData,
    browseSelectionState,
    clearBrowseSelectionState,
    deliveryDate,
    error: !menuData ? menuError : undefined,
    selectCategory,
    links,
    backLink,
  }
}
